import { tr } from "../i18n/LanguageContext";
import React, { useState, useEffect } from 'react';
import { apiErrorMessage } from "../lib/apiError";
import { useNavigate } from "../i18n/router";
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { formatDateRu } from '../lib/time';
import { 
  Shield, Users, Calendar, CheckCircle, XCircle, Clock, 
  ChevronDown, ChevronUp, Loader2, AlertTriangle 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Admin emails - direct check as fallback
const ADMIN_EMAILS = ['eunoia.community.eu@gmail.com'];

function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedApp, setExpandedApp] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  // Check admin access - use both role AND email check
  useEffect(() => {
    if (authLoading) return;
    
    // Check if user has admin access
    const isAdmin = user && (
      user.role === 'admin' || 
      ADMIN_EMAILS.includes(user.email)
    );
    
    setHasAccess(isAdmin);
    setAccessChecked(true);
    
    if (!user) {
      // Not logged in - redirect to login
      navigate('/login?redirect=/admin');
    } else if (!isAdmin) {
      // Logged in but not admin - redirect to home
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Fetch data when access is confirmed
  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const appsResponse = await fetch(
        `${BACKEND_URL}/api/admin/applications?status_filter=${statusFilter}`,
        { credentials: 'include' }
      );
      
      if (!appsResponse.ok) {
        if (appsResponse.status === 403) {
          setError(tr("Доступ запрещен"));
          return;
        }
        throw new Error('Failed to fetch applications');
      }
      
      const appsData = await appsResponse.json();
      setApplications(appsData);
      
      const statsResponse = await fetch(
        `${BACKEND_URL}/api/admin/stats`,
        { credentials: 'include' }
      );
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
      
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(tr("Ошибка загрузки данных"));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    setActionLoading(applicationId);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/applications/${applicationId}/approve`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(apiErrorMessage(data, 'Failed to approve'));
      }
      
      await fetchData();
    } catch (err) {
      console.error('Error approving application:', err);
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm(tr("Вы уверены, что хотите отклонить эту заявку?"))) return;
    
    setActionLoading(applicationId);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/applications/${applicationId}/reject`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(apiErrorMessage(data, 'Failed to reject'));
      }
      
      await fetchData();
    } catch (err) {
      console.error('Error rejecting application:', err);
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setActionLoading(null);
    }
  };

  // Show loading while checking auth
  if (authLoading || !accessChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{color: 'var(--eunoia-accent)'}} />
          <p className="eunoia-body">{tr("Проверка доступа...")}</p>
        </div>
      </div>
    );
  }

  // No access - this shouldn't show as we redirect, but just in case
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto mb-4" style={{color: 'var(--eunoia-soft)'}} />
          <p className="eunoia-body">{tr("Доступ запрещен")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
               style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
            <Shield className="h-7 w-7" style={{color: 'var(--eunoia-accent)'}} />
          </div>
          <div>
            <h1 className="eunoia-title-large">{tr("Панель администратора")}</h1>
            <p className="eunoia-caption">{tr("Управление заявками организаторов")}</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="eunoia-surface p-4 text-center">
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--eunoia-accent)'}}>
                {stats.applications.pending}
              </div>
              <div className="eunoia-caption">{tr("На рассмотрении")}</div>
            </div>
            <div className="eunoia-surface p-4 text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#16a34a'}}>
                {stats.applications.approved}
              </div>
              <div className="eunoia-caption">{tr("Одобрено")}</div>
            </div>
            <div className="eunoia-surface p-4 text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#dc2626'}}>
                {stats.applications.rejected}
              </div>
              <div className="eunoia-caption">{tr("Отклонено")}</div>
            </div>
            <div className="eunoia-surface p-4 text-center">
              <div className="text-3xl font-bold mb-1" style={{color: 'var(--eunoia-deep)'}}>
                {stats.totalOrganizers}
              </div>
              <div className="eunoia-caption">{tr("Организаторов")}</div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: 'pending', label: tr("На рассмотрении"), icon: Clock },
            { value: 'approved', label: tr("Одобренные"), icon: CheckCircle },
            { value: 'rejected', label: tr("Отклоненные"), icon: XCircle },
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                statusFilter === tab.value ? 'text-white' : 'bg-white/50 hover:bg-white/80'
              }`}
              style={{ backgroundColor: statusFilter === tab.value ? 'var(--eunoia-accent)' : undefined }}
            >
              <tab.icon className="h-4 w-4" />
              {tr(tab.label)}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="eunoia-surface p-6 text-center mb-6" style={{borderLeft: '4px solid #dc2626'}}>
            <AlertTriangle className="h-8 w-8 mx-auto mb-2" style={{color: '#dc2626'}} />
            <p>{error}</p>
            <button onClick={fetchData} className="mt-4 eunoia-btn-secondary">
              {tr("Попробовать снова")}
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && !error && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin" style={{color: 'var(--eunoia-accent)'}} />
          </div>
        )}

        {/* Applications List */}
        {!loading && !error && (
          <div className="space-y-4">
            {applications.length === 0 ? (
              <div className="eunoia-surface p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4" style={{color: 'var(--eunoia-accent-whisper)'}} />
                <p className="eunoia-body">{tr("Нет заявок в этой категории")}</p>
              </div>
            ) : (
              applications.map(app => (
                <div key={app.id} className="eunoia-surface overflow-hidden">
                  {/* Application Header */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-black/5 transition-colors"
                    onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center"
                             style={{backgroundColor: 'var(--eunoia-accent-whisper)'}}>
                          <span className="text-lg font-medium" style={{color: 'var(--eunoia-accent)'}}>
                            {(app.displayName || app.userDisplayName || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="eunoia-title-small">{app.displayName || app.userDisplayName}</h3>
                          <p className="eunoia-caption">{app.email || app.userEmail}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="eunoia-caption hidden sm:block">
                          {formatDateRu(app.createdAt)}
                        </span>
                        
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          app.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          app.status === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {app.status === 'pending' ? tr("Ожидает") :
                           app.status === 'approved' ? tr("Одобрена") : tr("Отклонена")}
                        </span>
                        
                        {expandedApp === app.id ? (
                          <ChevronUp className="h-5 w-5" style={{color: 'var(--eunoia-soft)'}} />
                        ) : (
                          <ChevronDown className="h-5 w-5" style={{color: 'var(--eunoia-soft)'}} />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedApp === app.id && (
                    <div className="px-6 pb-6 border-t" style={{borderColor: 'var(--eunoia-whisper)'}}>
                      <div className="pt-6 space-y-4">
                        <div>
                          <label className="eunoia-caption block mb-1">{tr("О себе")}</label>
                          <p className="eunoia-body p-3 rounded-lg" style={{backgroundColor: 'var(--eunoia-whisper)'}}>
                            {app.bio || tr("Не указано")}
                          </p>
                        </div>
                        
                        <div>
                          <label className="eunoia-caption block mb-1">{tr("Опыт организации")}</label>
                          <p className="eunoia-body p-3 rounded-lg" style={{backgroundColor: 'var(--eunoia-whisper)'}}>
                            {app.experience || tr("Не указано")}
                          </p>
                        </div>
                        
                        <div>
                          <label className="eunoia-caption block mb-1">{tr("Мотивация")}</label>
                          <p className="eunoia-body p-3 rounded-lg" style={{backgroundColor: 'var(--eunoia-whisper)'}}>
                            {app.motivation || tr("Не указано")}
                          </p>
                        </div>
                        
                        {/* Actions */}
                        {app.status === 'pending' && (
                          <div className="flex gap-3 pt-4">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(app.id); }}
                              disabled={actionLoading === app.id}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white transition-all hover:opacity-90 disabled:opacity-50"
                              style={{backgroundColor: '#16a34a'}}
                            >
                              {actionLoading === app.id ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="h-5 w-5" />
                                  {tr("Одобрить")}
                                </>
                              )}
                            </button>
                            
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReject(app.id); }}
                              disabled={actionLoading === app.id}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white transition-all hover:opacity-90 disabled:opacity-50"
                              style={{backgroundColor: '#dc2626'}}
                            >
                              <XCircle className="h-5 w-5" />
                              {tr("Отклонить")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
